export function getRepresentationValue(point, representation = 'standard') {
    if (representation === 'profileUsage') {
        return point.profileUsage ?? null;
    }
    if (representation === 'normalizedProfileUsage') {
        // The API retains a per-request value; scaling is presentation-only.
        return point.normalizedProfileUsage == null
            ? null
            : point.normalizedProfileUsage * 1000;
    }
    return point.metricValue;
}

export function getMissingFunctionRepresentationPoint(aggregatePoint) {
    return {
        metricValue: 0,
        profileUsage: aggregatePoint.profileUsage == null ? null : 0,
        requestCount: aggregatePoint.requestCount ?? null,
        normalizedProfileUsage:
            aggregatePoint.profileUsage != null
                && aggregatePoint.requestCount > 0
                ? 0
                : null,
    };
}

export function getRepresentationLabel(metricName, representation = 'standard') {
    if (representation === 'profileUsage') {
        return 'Profile usage';
    }
    if (representation === 'normalizedProfileUsage') {
        return 'Normalized profile usage';
    }
    return `${metricName} Usage`;
}

const superscriptCharacters = {
    '-': '\u207b',
    '+': '\u207a',
    '0': '\u2070',
    '1': '\u00b9',
    '2': '\u00b2',
    '3': '\u00b3',
    '4': '\u2074',
    '5': '\u2075',
    '6': '\u2076',
    '7': '\u2077',
    '8': '\u2078',
    '9': '\u2079',
};

function formatScientificValue(value, exponent) {
    const coefficient = value / (10 ** exponent);
    const roundedCoefficient = Number(coefficient.toPrecision(3));
    const superscriptExponent = [...String(exponent)]
        .map(character => superscriptCharacters[character])
        .join('');

    return `${roundedCoefficient} \u00d7 10${superscriptExponent}`;
}

export function formatRepresentationTooltipValue(value, formattedValue, representation = 'standard') {
    if (value === 0 || !Number.isFinite(value)) {
        return formattedValue;
    }

    const formattedNumber = Number(String(formattedValue).replace(/,/g, ''));
    if (Math.abs(value) >= 0.001
        && (!Number.isFinite(formattedNumber) || formattedNumber !== 0)) {
        return formattedValue;
    }

    const exponent = Math.floor(Math.log10(Math.abs(value)));
    return formatScientificValue(value, exponent);
}

export function formatNormalizedAxisTick(value, scaleMaximum) {
    if (value === 0 || !Number.isFinite(value)) {
        return '0';
    }

    const magnitude = Math.abs(scaleMaximum);
    if (magnitude >= 0.001) {
        return value;
    }

    const exponent = magnitude > 0
        ? Math.floor(Math.log10(magnitude))
        : Math.floor(Math.log10(Math.abs(value)));
    return formatScientificValue(value, exponent);
}

export function formatRepresentationAxisTick(value, scaleMaximum, representation = 'standard') {
    if (representation === 'normalizedProfileUsage') {
        return formatNormalizedAxisTick(value, scaleMaximum);
    }

    if (value !== 0 && Number.isFinite(value) && Math.abs(value) < 0.001) {
        const exponent = Math.floor(Math.log10(Math.abs(value)));
        return formatScientificValue(value, exponent);
    }

    return value;
}

export function getRepresentationCoverage(datasets) {
    const dates = new Map();

    for (const dataset of datasets) {
        for (const point of dataset.data ?? []) {
            const date = point.x instanceof Date ? point.x.getTime() : String(point.x);
            const isAvailable = point.y != null && Number.isFinite(point.y);
            dates.set(date, (dates.get(date) ?? false) || isAvailable);
        }
    }

    return {
        available: [...dates.values()].filter(Boolean).length,
        total: dates.size,
    };
}

export function getRepresentationUnavailableMessage(datasets, representation) {
    const unavailablePoints = datasets
        .flatMap(dataset => dataset.data ?? [])
        .filter(point => point.y == null);

    if (representation === 'profileUsage') {
        return 'Profile usage is unavailable because matching OnCPU sample evidence is missing.';
    }

    if (representation === 'normalizedProfileUsage') {
        const hasPendingRequests = unavailablePoints.some(point =>
            point.profileUsage != null && point.requestCount == null);
        const hasZeroRequests = unavailablePoints.some(point =>
            point.profileUsage != null && point.requestCount === 0);

        if (hasPendingRequests && hasZeroRequests) {
            return 'Normalized profile usage is unavailable for some dates while request volume is pending and for others because request volume is zero.';
        }
        if (hasPendingRequests) {
            return 'Normalized profile usage is unavailable while request volume is pending.';
        }
        if (hasZeroRequests) {
            return 'Normalized profile usage is undefined because request volume is zero.';
        }

        return 'Normalized profile usage is unavailable because matching OnCPU sample evidence is missing.';
    }

    return 'This representation is unavailable.';
}
